/**
 * A session whose access has ENDED: its organization membership was removed or
 * deactivated, or its customer was suspended or archived (a destroyed demo).
 *
 * The authorizer refuses every request such a session makes with `PERM005`
 * ("Access ended"). Before that code existed it refused them with `PERM001`, and
 * the dashboard did what it does with any refusal: it rendered one per panel —
 * "Couldn't load investigations — The list did not come back. Your cases are
 * still there." on every surface, each promising data the reader can no longer
 * reach, and each pointing at an administrator of an organization they have
 * left. It is not a panel's failure, it is the session's, so it is answered once
 * for the whole app (`<AccessEndedGate>`).
 *
 * Detected on the CLIENTS, not at call sites, for the reason `planRefusals.ts`
 * gives: every read and write in every package rides the one `ApiClient` (and
 * the GraphQL client), so a listener there sees all of them, including requests
 * made outside React. Wired once at bootstrap (`main.tsx`).
 *
 * LATCHED: once seen it stays until the page reloads. A response served from
 * the client's cache, or a request whose answer was already in flight, must not
 * bring the app back — the access is gone whatever a stale copy says.
 */
import * as React from "react";
import type { ApiClient } from "@authe/fe-commons/api";
import type { GraphQLClient } from "@authe/fe-commons/graphql";
import { errorCodeOf, toErrorResponse } from "@authe/fe-commons/errors";

/**
 * The catalogued code (`commons.errors.codes.PERM005`, mirrored in
 * `@authe/fe-commons/errors`).
 *
 * Spelled out rather than read off `ErrorCode.PERM005` at module load: a build
 * resolving an `@authe/fe-commons` that predates the code would otherwise crash
 * on import. The authorizer stamps it into `error_code` verbatim, which
 * {@link errorCodeOf} returns whether or not the catalogue knows it;
 * `accessEnded.test.ts` pins the two together.
 */
export const ACCESS_ENDED_CODE = "PERM005";

/** Is `error` the API saying this session's access has ended? */
export function isAccessEnded(error: unknown): boolean {
  return error != null && errorCodeOf(toErrorResponse(error).error) === ACCESS_ENDED_CODE;
}

/** What the latch remembers about the refusal that tripped it. */
export interface EndedAccess {
  /** The server's own reason ("Organization membership is no longer active", …). */
  reason: string | null;
}

let ended: EndedAccess | null = null;
const listeners = new Set<() => void>();

/**
 * Latch if `error` is an ended-access refusal. Safe to hand any error or `ok:false`
 * envelope; everything else is ignored, and so is every refusal after the first.
 */
export function noteAccessEnded(error: unknown): void {
  if (ended !== null || !isAccessEnded(error)) return;
  const message = toErrorResponse(error).error.message;
  ended = { reason: typeof message === "string" && message ? message : null };
  for (const listener of [...listeners]) listener();
}

/** The latched refusal, or `null` while access stands. */
export function endedAccess(): EndedAccess | null {
  return ended;
}

export function subscribeEndedAccess(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Re-renders when access ends; never goes back to `null` (see the module note). */
export function useEndedAccess(): EndedAccess | null {
  return React.useSyncExternalStore(subscribeEndedAccess, endedAccess, endedAccess);
}

/**
 * Watch both transports for the refusal. Returns the unsubscribe.
 *
 * GraphQL rarely carries it — an AppSync authorizer deny reaches the browser as
 * a bare `UnauthorizedException`, and the read then falls back to REST, which
 * does — but a resolver that forwards the REST payload as `errorInfo` would, so
 * it is watched too.
 */
export function watchForEndedAccess(
  apiClient: Pick<ApiClient, "onErrorEnvelope">,
  graphqlClient?: Pick<GraphQLClient, "onError"> | null,
): () => void {
  const offRest = apiClient.onErrorEnvelope(noteAccessEnded);
  const offGraphql = graphqlClient?.onError(noteAccessEnded);
  return () => {
    offRest();
    offGraphql?.();
  };
}

/** Tests only: forget the latch. Production never un-latches (see the module note). */
export function resetEndedAccessForTests(): void {
  ended = null;
}
